import { useState } from 'react';
import { Mail, Plus, X } from 'lucide-react';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const todayEt = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

const EmailRow = ({ e, saving, onChange, onRemove }) => (
  <li className={`text-xs rounded px-2 py-1.5 ${e.disabled ? 'bg-slate-900/30 text-slate-500' : 'bg-slate-900/60 text-slate-300'}`} data-testid={`autotri-email-${e.email}`}>
    <div className="flex items-center justify-between">
      <span className={`truncate ${e.disabled ? 'line-through' : ''}`}>{e.email}</span>
      <button onClick={onRemove} disabled={saving} className="text-slate-500 hover:text-rose-400 transition-colors ml-2"
        title="Delete entry" data-testid={`autotri-email-remove-${e.email}`}><X className="w-3.5 h-3.5" /></button>
    </div>
    <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
      <span>Start</span>
      <input type="date" value={e.start_date || ''} disabled={saving} onChange={(ev) => onChange({ start_date: ev.target.value || null })}
        className="bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-slate-300" data-testid={`autotri-email-start-${e.email}`} />
      <label className="flex items-center gap-1 cursor-pointer ml-auto">
        <input type="checkbox" checked={!!e.disabled} disabled={saving} onChange={(ev) => onChange({ disabled: ev.target.checked })}
          className="w-3.5 h-3.5 accent-rose-500" data-testid={`autotri-email-disabled-${e.email}`} />
        Disabled{e.disabled && e.disabled_at ? ` ${e.disabled_at}` : ''}
      </label>
    </div>
  </li>
);

export const AlertEmailsBox = ({ emails, saving, onSave }) => {
  const [val, setVal] = useState('');
  const [start, setStart] = useState(todayEt());
  const [err, setErr] = useState('');
  const add = () => {
    const e = val.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) { setErr('Enter a valid email'); return; }
    if (emails.some((x) => x.email === e)) { setErr('Already on the list'); return; }
    setErr(''); setVal('');
    onSave([...emails, { email: e, start_date: start || null, disabled: false }]);
  };
  const update = (email, patch) => onSave(emails.map((x) => (x.email === email ? { ...x, ...patch } : x)));
  const remove = (email) => {
    if (!window.confirm(`Delete ${email} entirely? (Use "Disabled" instead to keep their dashboard history.)`)) return;
    onSave(emails.filter((x) => x.email !== email));
  };
  const active = emails.filter((x) => !x.disabled).length;
  return (
    <div className="w-full xl:w-96 shrink-0 bg-slate-800/50 border border-slate-700 rounded-lg p-3 self-start" data-testid="autotri-alert-emails">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-slate-400" />
        <span className="text-sm font-semibold text-slate-200">Email distribution</span>
        <span className="text-xs text-slate-500">({active} active / {emails.length})</span>
      </div>
      <p className="text-xs text-slate-500 mb-2">Active members get every Auto Tri email from their start date and can open the TriBet Dashboard (start → disabled date).</p>
      <ul className="space-y-1 mb-2">
        {active === 0 && <li className="text-xs text-amber-400">No active recipients — emails will not be sent.</li>}
        {emails.map((e) => (
          <EmailRow key={e.email} e={e} saving={saving} onChange={(p) => update(e.email, p)} onRemove={() => remove(e.email)} />
        ))}
      </ul>
      <div className="flex gap-2">
        <input value={val} onChange={(e) => { setVal(e.target.value); setErr(''); }} onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="name@example.com" type="email"
          className="flex-1 min-w-0 px-2 py-1.5 bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded-lg focus:outline-none focus:border-emerald-500"
          data-testid="autotri-email-input" />
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} title="Start date"
          className="px-1 py-1.5 bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded-lg" data-testid="autotri-email-new-start" />
        <button onClick={add} disabled={saving} className="px-2 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs flex items-center gap-1 transition-colors"
          data-testid="autotri-email-add-btn"><Plus className="w-3 h-3" />Add</button>
      </div>
      {err && <p className="text-xs text-rose-400 mt-1" data-testid="autotri-email-error">{err}</p>}
    </div>
  );
};
